import { useEffect, useMemo, useState } from "react";
import { setReminder } from "./reminder-api";

interface Props {
  sessionId: string;
  currentReminderDateMs?: number | null;
  onSaved?: (nextDate: number | null) => void;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function toInputValue(ms: number): string {
  const d = new Date(ms);
  const y = d.getUTCFullYear().toString().padStart(4, "0");
  const m = (d.getUTCMonth() + 1).toString().padStart(2, "0");
  const day = d.getUTCDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fromInputValue(s: string): number {
  const [y, m, d] = s.split("-").map((x) => Number(x));
  return Date.UTC(y, m - 1, d);
}

function formatLong(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleDateString(undefined, {
    timeZone: "UTC",
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function ReminderPicker({ sessionId, currentReminderDateMs, onSaved }: Props) {
  const hasFutureReminder =
    typeof currentReminderDateMs === "number" && currentReminderDateMs > Date.now();

  const defaultDateMs = useMemo(
    () => (hasFutureReminder ? (currentReminderDateMs as number) : Date.now() + 90 * DAY_MS),
    [hasFutureReminder, currentReminderDateMs],
  );

  const [mode, setMode] = useState<"view" | "edit">(hasFutureReminder ? "view" : "edit");
  const [dateStr, setDateStr] = useState<string>(toInputValue(defaultDateMs));
  const [busy, setBusy] = useState<null | "saving" | "clearing">(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMode(hasFutureReminder ? "view" : "edit");
    setDateStr(toInputValue(defaultDateMs));
  }, [hasFutureReminder, defaultDateMs]);

  const minDateStr = toInputValue(Date.now() + DAY_MS);

  async function save() {
    setError(null);
    if (!dateStr) {
      setError("Pick a date first.");
      return;
    }
    const ms = fromInputValue(dateStr);
    if (!Number.isFinite(ms) || ms <= Date.now()) {
      setError("Pick a date in the future.");
      return;
    }
    setBusy("saving");
    try {
      const { nextReviewDate } = await setReminder(sessionId, ms);
      onSaved?.(nextReviewDate);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function clear() {
    setError(null);
    setBusy("clearing");
    try {
      await setReminder(sessionId, null);
      onSaved?.(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="reminder-picker">
      {mode === "view" && hasFutureReminder ? (
        <div className="reminder-picker__scheduled">
          <div className="reminder-picker__stamp">Scheduled</div>
          <div className="reminder-picker__date">
            {formatLong(currentReminderDateMs as number)}
          </div>
          <p className="reminder-picker__note">
            We'll email you on this date with an ICS attachment you can drop into your calendar.
          </p>
          <div className="reminder-picker__actions">
            <button
              type="button"
              className="btn btn--text"
              onClick={() => setMode("edit")}
              disabled={busy !== null}
            >
              Change date
            </button>
            <span className="reminder-picker__sep" aria-hidden="true">·</span>
            <button
              type="button"
              className="btn btn--text"
              onClick={clear}
              disabled={busy !== null}
            >
              {busy === "clearing" ? "Clearing…" : "Clear reminder"}
            </button>
          </div>
        </div>
      ) : (
        <div className="reminder-picker__edit">
          <label className="reminder-picker__label" htmlFor={`reminder-${sessionId}`}>
            Pick a date
          </label>
          <input
            id={`reminder-${sessionId}`}
            className="reminder-picker__input"
            type="date"
            value={dateStr}
            min={minDateStr}
            onChange={(e) => setDateStr(e.target.value)}
            disabled={busy !== null}
          />
          <div className="reminder-picker__actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={save}
              disabled={busy !== null}
            >
              {busy === "saving" ? "Saving…" : "Save reminder"}
            </button>
            {hasFutureReminder ? (
              <button
                type="button"
                className="btn btn--text"
                onClick={() => setMode("view")}
                disabled={busy !== null}
              >
                Cancel
              </button>
            ) : (
              <button
                type="button"
                className="btn btn--text"
                onClick={() => onSaved?.(null)}
                disabled={busy !== null}
              >
                Skip for now
              </button>
            )}
          </div>
        </div>
      )}

      {error && <div className="banner banner--error reminder-picker__error">{error}</div>}
    </div>
  );
}
